/**
 * Block Parser
 *
 * Detects and parses block markers in streaming content.
 * Handles both JSON-based blocks [TYPE]{json}[/TYPE] and
 * colon-syntax blocks [TYPE:value].
 */

import type { MessageBlock } from '../config';
import { hasBlockRenderer } from '../../ui/blocks/index';
import { tryParseJSON, deepParseJSON } from './json-utils';
import { VALID_BLOCK_TYPES, COLON_SYNTAX_TYPES, BLOCK_TYPE_NAMES } from './constants';

// =============================================================================
// TYPES
// =============================================================================

export interface ParsedColonBlock {
  type: string;
  block: MessageBlock | null;
  startIndex: number;
  endIndex: number;
}

export interface ParsedJsonBlock {
  type: string;
  block: MessageBlock | null;
  startIndex: number;
  endIndex: number;
  error?: string;
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Check if a block type is valid.
 * Checks both built-in types and custom blocks registered in the block registry.
 */
export function isValidBlockType(type: string): boolean {
  // Check built-in types first (faster)
  if (VALID_BLOCK_TYPES.includes(type.toUpperCase() as typeof VALID_BLOCK_TYPES[number])) {
    return true;
  }
  // Check if a custom block renderer is registered for this type
  return hasBlockRenderer(type);
}

/**
 * Check if a block type uses colon syntax.
 */
export function isColonSyntaxBlockType(type: string): boolean {
  return COLON_SYNTAX_TYPES.includes(type.toUpperCase() as typeof COLON_SYNTAX_TYPES[number]);
}

/**
 * Format block type for display in loading placeholders.
 */
export function formatBlockType(type: string): string {
  return BLOCK_TYPE_NAMES[type.toUpperCase()] || 'content';
}

// =============================================================================
// COLON-SYNTAX BLOCK PARSING
// =============================================================================

/**
 * Parse a colon-syntax block like [LIVE_SECTION:players] or [FLOW_FORM:loan-0]
 */
export function parseColonSyntaxBlock(
  blockType: string,
  value: string
): MessageBlock | null {
  const normalizedType = blockType.toLowerCase().replace(/_/g, '-');
  const trimmedValue = value.trim();

  if (normalizedType === 'live-section') {
    return {
      type: 'live-section',
      data: { id: trimmedValue }
    };
  }

  if (normalizedType === 'flow-form') {
    // Try pipe-separated format first: flowId|groupIndex
    const pipeParts = trimmedValue.match(/^([^|]+)\|(\d+)$/);
    if (pipeParts) {
      return {
        type: 'flow-form',
        data: { flowId: pipeParts[1]!.trim(), groupIndex: parseInt(pipeParts[2]!, 10) }
      };
    }

    // Try dash-separated format: flowId-groupIndex
    const dashParts = trimmedValue.match(/^(.+)-(\d+)$/);
    if (dashParts) {
      return {
        type: 'flow-form',
        data: { flowId: dashParts[1]!, groupIndex: parseInt(dashParts[2]!, 10) }
      };
    }

    // Default: use whole value as flowId with groupIndex 0
    return {
      type: 'flow-form',
      data: { flowId: trimmedValue, groupIndex: 0 }
    };
  }

  if (normalizedType === 'demo') {
    // [DEMO:xxx] markers are deprecated - use CARDS with link: actions instead
    console.warn('[BlockParser] Deprecated [DEMO:xxx] marker ignored:', trimmedValue);
    return null;
  }

  console.warn('[BlockParser] Unknown colon-syntax block:', blockType, value);
  return null;
}

// =============================================================================
// JSON BLOCK PARSING
// =============================================================================

/**
 * Parse a JSON-based block from its raw content string.
 * Input should be the full block including markers: [TYPE]{json}[/TYPE]
 */
export function parseJsonBlock(blockContent: string): ParsedJsonBlock | null {
  const blockPattern = /\[([A-Z_-]+)\]([\s\S]*?)\[\/\1\]/i;
  const match = blockContent.match(blockPattern);

  if (!match) {
    return null;
  }

  const blockType = match[1]!.toLowerCase();
  const blockDataStr = match[2]!.trim();
  const blockData = tryParseJSON(blockDataStr);

  if (blockData !== null) {
    return {
      type: blockType,
      block: {
        type: blockType,
        data: deepParseJSON(blockData),
      },
      startIndex: 0,
      endIndex: blockContent.length
    };
  }

  return {
    type: blockType,
    block: null,
    startIndex: 0,
    endIndex: blockContent.length,
    error: `Failed to parse JSON for ${blockType} block`
  };
}

// =============================================================================
// CONTENT STRIPPING
// =============================================================================

/**
 * Strip incomplete and malformed block markers from text.
 *
 * Handles cases like:
 * - `[INTERACTIVE]- Filter by...` (no JSON, no closing tag)
 * - `[STATS]` at end of content (incomplete)
 * - Orphan opening tags without closing tags
 */
export function stripIncompleteBlockMarkers(text: string): string {
  let result = text;

  // 1. Strip malformed JSON blocks: [TYPE] followed by non-JSON content until end or next block
  for (const blockType of VALID_BLOCK_TYPES) {
    const malformedPattern = new RegExp(
      `\\[${blockType}\\](?!\\s*\\{)([^\\[]*?)(?=\\[(?:[A-Z_-]+[:\\]])|$)`,
      'gi'
    );

    result = result.replace(malformedPattern, (match, _content) => {
      const hasClosingTag = new RegExp(`\\[\\/${blockType}\\]`, 'i').test(
        result.substring(result.indexOf(match) + match.length)
      );
      if (!hasClosingTag) {
        console.warn(`[BlockParser] Stripping malformed ${blockType} block:`, match.substring(0, 50));
        return '';
      }
      return match;
    });
  }

  // 2. Remove incomplete block at the end (partial tag like `[INTER`)
  const incompletePattern = /\[([A-Z_-]+)(?:\](?:(?!\[\/\1\]).)*)?$/i;
  result = result.replace(incompletePattern, '');

  // 3. Remove orphan block markers (opening tag without any closing bracket)
  const orphanPattern = /\[([A-Z_-]+)(?![^\[]*\])/gi;
  result = result.replace(orphanPattern, (match, type) => {
    if (VALID_BLOCK_TYPES.includes(type.toUpperCase() as typeof VALID_BLOCK_TYPES[number])) {
      return '';
    }
    return match;
  });

  // 4. Clean up any trailing bullet points that were part of malformed blocks
  result = result.replace(/^\s*-\s+(?:Filter|Book|View|Contact|Request|Schedule|Apply)[^\n]*$/gim, '');

  // 5. Clean up extra whitespace/newlines left behind
  result = result.replace(/\n{3,}/g, '\n\n').trim();

  return result;
}

// =============================================================================
// MARKER DETECTION
// =============================================================================

/**
 * Check if content ends with a potential block marker that should be withheld.
 * Returns the index where the partial marker starts, or -1 if none found.
 */
export function findPartialBlockMarker(content: string): number {
  // Check for partial opening marker like `[STAT` or just `[`
  const potentialBlockMatch = content.match(/\[([A-Z_-]*)$/i);

  if (potentialBlockMatch && potentialBlockMatch.index !== undefined) {
    const partialType = potentialBlockMatch[1]?.toUpperCase() || '';

    // Hide if empty (just '[') or if it's a prefix of any valid block type
    const couldBeBlock = partialType === '' ||
      VALID_BLOCK_TYPES.some(type => type.startsWith(partialType));

    if (couldBeBlock) {
      return potentialBlockMatch.index;
    }
  }

  // Check for incomplete colon-syntax markers
  const incompleteColonMatch = content.match(/\[(FLOW[-_]FORM|LIVE[-_]SECTION|DEMO):[^\]]*$/i);
  if (incompleteColonMatch && incompleteColonMatch.index !== undefined) {
    return incompleteColonMatch.index;
  }

  return -1;
}

/**
 * Cached regex for stripping malformed markers.
 * Built dynamically from VALID_BLOCK_TYPES to stay in sync.
 */
let malformedMarkersRegex: RegExp | null = null;

function getMalformedMarkersRegex(): RegExp {
  if (!malformedMarkersRegex) {
    // Filter out colon-syntax types since they don't use JSON
    const jsonBlockTypes = VALID_BLOCK_TYPES.filter(
      type => !COLON_SYNTAX_TYPES.includes(type as typeof COLON_SYNTAX_TYPES[number])
    );
    const pattern = `\\[(${jsonBlockTypes.join('|')})\\](?!\\s*\\{)[^[]*(?=\\[|$)`;
    malformedMarkersRegex = new RegExp(pattern, 'gi');
  }
  return malformedMarkersRegex;
}

/**
 * Strip malformed block markers from streaming text.
 * Pattern: [BLOCK_TYPE] followed by non-JSON content (starts with - or text, not {)
 */
export function stripMalformedStreamingMarkers(text: string): string {
  const regex = getMalformedMarkersRegex();
  // Reset lastIndex since we reuse the regex
  regex.lastIndex = 0;
  return text.replace(regex, '');
}
